/**
 * Identity headers for the legacy axios transport.
 *
 * `lib/fetcher.js` and the store modules that call axios directly each build
 * their own header object, so there is no single place to add a header to. An
 * interceptor on the default axios instance covers all of them at once; the
 * application never calls `axios.create()`, so every request passes through
 * here.
 *
 * The other transport, `lib/http/client.js`, sends the same header from its own
 * `buildHeaders`.
 */

import axios from 'axios';
import { readUsername } from '../lib/session';

/**
 * @type {string}
 */
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '';

/**
 * Reports whether a request is bound for our own API.
 *
 * Every call site today prefixes `VUE_APP_API_BASE_URL`, but the check keeps a
 * future call to a third party from receiving the username, which would leak it
 * to an unrelated host and add an avoidable header to its CORS preflight.
 *
 * @param {Object} config - Axios request configuration.
 * @returns {boolean} True when the request targets the configured API.
 */
const targetsApi = (config) => {
  if (API_BASE_URL === '') return false;

  const url = `${config.baseURL || ''}${config.url || ''}`;
  return url.startsWith(API_BASE_URL);
};

/**
 * Registers the interceptor. Call once while the application boots.
 *
 * @returns {void}
 */
export default function initAxiosHeaders() {
  axios.interceptors.request.use((config) => {
    if (!targetsApi(config)) return config;

    const username = readUsername();
    if (!username) return config;

    return { ...config, headers: { ...config.headers, 'X-User': username } };
  });
}
